/**
 * The sporting catalogue the fixture world draws from.
 *
 * Names are taken from the fixtures visible in the source recording (The
 * Hundred, LPL, CPL, county cricket, ATP) so the console demos against events an
 * operator would actually recognise, rather than "Team A v Team B".
 */

import type { MarketType, Sport } from './types'

export interface MarketTemplate {
  name: string
  type: MarketType
  selections: string[]
  /** Relative likelihood a bet lands on this market. */
  weight: number
}

export interface EventTemplate {
  name: string
  sport: Sport
  competition: string
  markets: MarketTemplate[]
}

const twoWay = (home: string, away: string): MarketTemplate[] => [
  { name: 'Match Odds', type: 'Match Odds', selections: [home, away], weight: 34 },
  { name: 'Bookmaker', type: 'Bookmaker', selections: [home, away], weight: 26 },
  { name: 'Toss Market', type: 'Toss Market', selections: [home, away], weight: 12 },
]

const fancy = (labels: string[]): MarketTemplate[] =>
  labels.map((name) => ({
    name,
    type: name.startsWith('Premium') ? ('Premium Fancy' as const) : ('Fancy' as const),
    selections: ['Yes', 'No'],
    weight: 8,
  }))

const cricket = (
  competition: string,
  home: string,
  away: string,
  lines: string[],
): EventTemplate => ({
  name: `${home} v ${away}`,
  sport: 'Cricket',
  competition,
  markets: [...twoWay(home, away), ...fancy(lines)],
})

export const EVENT_TEMPLATES: EventTemplate[] = [
  cricket('The Hundred', 'Birmingham Phoenix W', 'Welsh Fire W', [
    '6 Over Runs BPH W',
    '1 Over Runs WEF W',
    'Fall Of 1st Wkt BPH W',
  ]),
  cricket('The Hundred', 'Southern Brave', 'Birmingham Phoenix', [
    '20 Ball Runs SOB',
    'Total Match Sixes',
    'Premium Fancy — Top Batter Runs SOB',
  ]),
  cricket('The Hundred', 'Manchester Super Giants', 'Trent Rockets', [
    '1 To 100 Balls Runs MSG',
    'Highest Over Runs',
  ]),
  cricket('The Hundred', 'Manchester Super Giants W', 'Trent Rockets W', [
    '6 Over Runs MSG W',
    'Fall Of 1st Wkt TRO W',
  ]),
  cricket('The Hundred', 'MI London', 'London Spirit', [
    '1 To 100 Balls Runs MIL',
    'Premium Fancy — Match 4s MIL',
  ]),
  cricket('The Hundred', 'MI London W', 'London Spirit W', ['10 Over Runs MIL W']),
  cricket('Lanka Premier League', 'Jaffna Kings', 'Kandy Royals', [
    '6 Over Runs JAF',
    'Fall Of 1st Wkt KAN',
  ]),
  cricket('Caribbean Premier League', 'Guyana Amazon Warriors', 'Perth Scorchers', [
    '6 Over Runs GAW',
    '1 Over Runs PER',
  ]),
  cricket('County Championship', 'Lancashire', 'Nottinghamshire', [
    '1 Over Runs LAN',
    'Session 1 Runs LAN',
    'Premium Fancy — Lead Runs',
  ]),
  cricket('County Championship', 'Surrey', 'Somerset', ['1 Over Runs SUR', 'Session 1 Runs SUR']),
  {
    name: 'C Norrie v J Duckworth',
    sport: 'Tennis',
    competition: 'ATP Washington',
    markets: [
      { name: 'Match Odds', type: 'Match Odds', selections: ['C Norrie', 'J Duckworth'], weight: 40 },
      { name: 'Bookmaker', type: 'Bookmaker', selections: ['C Norrie', 'J Duckworth'], weight: 20 },
    ],
  },
  {
    name: 'L Musetti v B Shelton',
    sport: 'Tennis',
    competition: 'ATP Toronto',
    markets: [
      { name: 'Match Odds', type: 'Match Odds', selections: ['L Musetti', 'B Shelton'], weight: 40 },
      { name: 'Bookmaker', type: 'Bookmaker', selections: ['L Musetti', 'B Shelton'], weight: 20 },
    ],
  },
  {
    name: 'Arsenal v Chelsea',
    sport: 'Soccer',
    competition: 'Premier League',
    markets: [
      {
        name: 'Match Odds',
        type: 'Match Odds',
        selections: ['Arsenal', 'Chelsea', 'The Draw'],
        weight: 40,
      },
      {
        name: 'Bookmaker',
        type: 'Bookmaker',
        selections: ['Arsenal', 'Chelsea', 'The Draw'],
        weight: 22,
      },
      { name: 'Over/Under 2.5 Goals', type: 'Fancy', selections: ['Over 2.5', 'Under 2.5'], weight: 14 },
    ],
  },
  {
    name: 'Real Madrid v Sevilla',
    sport: 'Soccer',
    competition: 'La Liga',
    markets: [
      {
        name: 'Match Odds',
        type: 'Match Odds',
        selections: ['Real Madrid', 'Sevilla', 'The Draw'],
        weight: 40,
      },
      { name: 'Over/Under 2.5 Goals', type: 'Fancy', selections: ['Over 2.5', 'Under 2.5'], weight: 16 },
    ],
  },
  {
    name: 'Ascot 15:40 — 1m2f Handicap',
    sport: 'Horse Racing',
    competition: 'Ascot',
    markets: [
      {
        name: 'Win Market',
        type: 'Match Odds',
        selections: [
          'Silver Kestrel',
          'Monsoon Lily',
          'Court Of Session',
          'Bright Ember',
          'Nordic Anthem',
          'Palace Rumour',
          'Ivory Gambit',
        ],
        weight: 46,
      },
    ],
  },
  {
    name: 'Newcastle 19:05 — Novice Stakes',
    sport: 'Horse Racing',
    competition: 'Newcastle',
    markets: [
      {
        name: 'Win Market',
        type: 'Match Odds',
        selections: [
          'Harbour Lantern',
          'Deep Crossing',
          'Turn Of Phrase',
          'Copper Verdict',
          'Winter Ledger',
          'Salt And Ash',
        ],
        weight: 46,
      },
    ],
  },
  {
    name: 'Romford 18:22 — A3 480m',
    sport: 'Greyhound Racing',
    competition: 'Romford',
    markets: [
      {
        name: 'Win Market',
        type: 'Match Odds',
        selections: ['Trap 1', 'Trap 2', 'Trap 3', 'Trap 4', 'Trap 5', 'Trap 6'],
        weight: 46,
      },
    ],
  },
  {
    name: 'Evolution — Lightning Roulette',
    sport: 'Casino',
    competition: 'Live Casino',
    markets: [
      // Red/Black, not the wheel's own 'Player'/'House': the leaf tier in this
      // console renders as MEMBER and the word "Player" may never reach a user-facing
      // string, and these markets print their selection names verbatim. Red and
      // Black are also what a roulette table actually offers.
      { name: 'Table Round', type: 'Live Casino', selections: ['Red', 'Black'], weight: 50 },
    ],
  },
  {
    name: 'Ezugi — Andar Bahar',
    sport: 'Casino',
    competition: 'Card Game',
    markets: [
      { name: 'Table Round', type: 'Live Casino', selections: ['Andar', 'Bahar'], weight: 50 },
    ],
  },
]

/**
 * Username stems observed in the source product, kept because they carry the
 * real texture of this data — currency suffixes, demo accounts, role suffixes.
 */
export const USERNAME_STEMS = [
  'alex',
  'alpha',
  'artemis',
  'avatar',
  'bowie',
  'casino',
  'cooper',
  'daniel',
  'danny',
  'danrec',
  'fprint',
  'fundist',
  'hardik',
  'jack',
  'jacksparrow',
  'km34',
  'kun',
  'lee',
  'luna',
  'rocco',
  'nova',
  'orion',
  'pixel',
  'quartz',
  'ranger',
  'saffron',
  'tundra',
  'umber',
  'vesper',
  'wraith',
  'yukon',
  'zephyr',
]

export const DISPLAY_FIRST = [
  'Aarav',
  'Vihaan',
  'Ishaan',
  'Kabir',
  'Rohan',
  'Arjun',
  'Dev',
  'Kunal',
  'Manav',
  'Nikhil',
  'Priya',
  'Ananya',
  'Diya',
  'Meera',
  'Riya',
  'Sana',
  'Tara',
  'Zoya',
  'Farhan',
  'Imran',
  'Zaid',
  'Yusuf',
  'Aditya',
  'Varun',
]

export const DISPLAY_LAST = [
  'Shah',
  'Patel',
  'Mehta',
  'Iyer',
  'Rao',
  'Nair',
  'Kapoor',
  'Chawla',
  'Bhatt',
  'Sethi',
  'Malhotra',
  'Joshi',
  'Reddy',
  'Bose',
  'Chauhan',
  'Dutta',
]

export const TRANSFER_REMARKS_GIVE = [
  'Weekly top-up',
  'Opening credit',
  'Settlement advance',
  'Limit increase agreed on call',
  'Festival top-up',
  'Cash collected — credited',
]

export const TRANSFER_REMARKS_TAKE = [
  'Weekly settlement',
  'Cash handed over',
  'Reducing exposure',
  'Month-end clear-down',
  'Partial recovery',
  'Closing balance pulled back',
]

export const DEVICES = [
  'Android · Chrome',
  'iPhone · Safari',
  'Windows · Chrome',
  'Android · Strix App',
  'macOS · Safari',
]
